import { useMemo, type JSX } from "react";
import { highlightTree, tagHighlighter } from "@lezer/highlight";
import { pythonLanguage } from "@codemirror/lang-python";
import { tags } from "@lezer/highlight";

interface SyntaxHighlightedCodeProps {
  code: string;
  className?: string;
}

// Create a highlighter that matches ExpressionEditor's color scheme
const pythonHighlighter = tagHighlighter([
  { tag: tags.keyword, class: "text-[#c678dd]" },
  { tag: tags.operator, class: "text-[#56b6c2]" },
  { tag: tags.string, class: "text-[#98c379]" },
  { tag: tags.number, class: "text-[#d19a66]" },
  { tag: tags.bool, class: "text-[#d19a66]" },
  { tag: tags.null, class: "text-[#d19a66]" },
  { tag: tags.comment, class: "text-[#5c6370] italic" },
  { tag: tags.function(tags.variableName), class: "text-[#61afef]" },
  { tag: tags.variableName, class: "text-[#e06c75]" },
  { tag: tags.propertyName, class: "text-[#e06c75]" },
]);

/**
 * Python syntax highlighter using CodeMirror's parser
 * Ensures consistency with the ExpressionEditor's syntax highlighting
 */
export function SyntaxHighlightedCode({
  code,
  className = "",
}: SyntaxHighlightedCodeProps) {
  const highlighted = useMemo(() => {
    try {
      // Parse the code using Python language parser
      const tree = pythonLanguage.parser.parse(code);

      const elements: JSX.Element[] = [];
      let pos = 0;

      // Traverse the syntax tree and collect highlighted spans
      highlightTree(
        tree,
        pythonHighlighter,
        (from: number, to: number, classes: string) => {
          // Add any text before this token
          if (from > pos) {
            elements.push(
              <span key={`plain-${pos}`}>{code.slice(pos, from)}</span>,
            );
          }

          // Add the highlighted token
          const tokenText = code.slice(from, to);
          elements.push(
            <span key={`token-${from}`} className={classes}>
              {tokenText}
            </span>,
          );

          pos = to;
        },
      );

      // Add any remaining text
      if (pos < code.length) {
        elements.push(<span key={`plain-end`}>{code.slice(pos)}</span>);
      }

      return elements.length > 0
        ? elements
        : [<span key="fallback">{code}</span>];
    } catch {
      // Fallback to plain text if parsing fails
      return [<span key="fallback">{code}</span>];
    }
  }, [code]);

  return <code className={className}>{highlighted}</code>;
}
